import { useCallback, useEffect, useState } from "react";
import { useRoomContext } from "@livekit/components-react";
import type { RemoteParticipant, RemoteTrackPublication } from "livekit-client";
import {
  ConnectionState,
  DisconnectReason,
  RoomEvent,
  Track,
} from "livekit-client";
import { t } from "@/i18n-js/instance";
import { useToast } from "@circle-react-uikit/ToastV2";
import { useLivekitLocalParticipant } from "./useLivekitLocalParticipant";

const I18N_ERROR_BASE = "live_streams.room.livekit_errors.";
const errorTranslation = (translationKey: string) =>
  `${I18N_ERROR_BASE}${translationKey}`;
const RECONNECTING_TOAST_ID = "reconnecting-toast";

export const useLivekitErrorHandler = ({
  onDisconnect,
}: {
  onDisconnect: () => void;
}) => {
  const [isReconnecting, setIsReconnecting] = useState(false);
  const { error, success, remove } = useToast();
  const room = useRoomContext();
  const localParticipant = useLivekitLocalParticipant();

  const handleMediaDevicesError = useCallback(() => {
    if (localParticipant.lastCameraError) {
      error(localParticipant.lastCameraError.message);
    } else if (localParticipant.lastMicrophoneError) {
      error(localParticipant.lastMicrophoneError.message);
    }
  }, [error, localParticipant]);

  const handleDisconnected = useCallback(
    (reason?: DisconnectReason) => {
      const errorMap: Partial<Record<DisconnectReason, string>> = {
        [DisconnectReason.DUPLICATE_IDENTITY]: t(
          errorTranslation("duplicate_identity"),
        ),
        [DisconnectReason.JOIN_FAILURE]: t(errorTranslation("join_failure")),
        [DisconnectReason.SIGNAL_CLOSE]: t(errorTranslation("signal_close")),
        [DisconnectReason.UNKNOWN_REASON]: t(
          errorTranslation("unknown_reason"),
        ),
      };

      if (reason && errorMap[reason]) {
        error(errorMap[reason] || "");
      }
    },
    [error],
  );

  const handleConnectionStateChange = useCallback(
    (state: ConnectionState) => {
      if (state === ConnectionState.SignalReconnecting && !isReconnecting) {
        setIsReconnecting(true);
        error(t(errorTranslation("reconnecting")), {
          id: RECONNECTING_TOAST_ID,
          shouldAutohide: false,
        });
      } else if (state === ConnectionState.Connected && isReconnecting) {
        setIsReconnecting(false);
        remove(RECONNECTING_TOAST_ID);
        success(t(errorTranslation("reconnected")));
      } else if (state === ConnectionState.Disconnected && isReconnecting) {
        setIsReconnecting(false);
        remove(RECONNECTING_TOAST_ID);
        error(t(errorTranslation("disconnected")));
        onDisconnect && onDisconnect();
      }
    },
    [error, remove, success, isReconnecting, onDisconnect],
  );

  const handleTrackUnsubscribed = useCallback(
    (
      track: Track,
      _publication: RemoteTrackPublication,
      participant: RemoteParticipant,
    ) => {
      if (track.kind === Track.Kind.Audio) {
        console.warn(
          "Livekit: Audio track unsubscribed:",
          participant.identity,
        );
      }
    },
    [],
  );

  useEffect(() => {
    if (!room) return;

    room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChange);
    room.on(RoomEvent.Disconnected, handleDisconnected);
    room.on(RoomEvent.MediaDevicesError, handleMediaDevicesError);
    room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);

    return () => {
      room.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChange);
      room.off(RoomEvent.Disconnected, handleDisconnected);
      room.off(RoomEvent.MediaDevicesError, handleMediaDevicesError);
      room.off(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
    };
  }, [
    room,
    handleConnectionStateChange,
    handleDisconnected,
    handleMediaDevicesError,
    handleTrackUnsubscribed,
  ]);
};
